export type UpgradeState = {
  damage: number;
  critChance: number; // 0..1
  critMultiplier: number; // e.g. 2 = 200%
  splitChance: number; // 0..1 chance to emit split projectiles on hit
  splitDiminishing: number; // damage multiplier per split hop
  splitCount: number; // how many split projectiles to emit on proc
  bounceChance: number; // 0..1 to bounce on hit
  bounceDiminishing: number; // damage multiplier per bounce
  bounceMaxTargets: number; // how many enemies total per projectile (including first)
  attackSpeedMult: number; // additive percent multiplier to fire rate (e.g., 0.2 = +20%)
  rangeBonus: number; // additional range in pixels
  // Rage mechanic upgrades
  rageChance: number; // 0..1 chance to enter rage on attack
  rageDuration: number; // seconds, base 2.0 and +0.1 per upgrade
};

export type SkillsState = {
  moneyMultiChance: number; // chance to multiply money per kill
  moneyMultiAmount: number; // multiplier if triggered
  waveBonusMin: number; // min bonus gold per completed wave
  waveBonusMax: number; // max bonus gold per completed wave
};

export type CostsState = {
  damage: number;
  critChance: number;
  critMult: number;
  splitChance: number;
  splitDim: number;
  splitCount: number;
  bounceChance: number;
  bounceDim: number;
  bounceTargets: number;
  attackSpeed: number;
  range: number;
  rageChance: number;
  rageDuration: number;
};

export class GameState {
  gold = 0;
  wave = 0;
  waveStatus: string = '';
  timescale = 1; // persisted game speed (1..5)
  // Persist tower vitals in game state so UI and saves can access them
  maxHp = 200;
  hp = 200;
  maxMana = 100;
  mana = 0;
  // Runtime-only rage info for UI (not persisted)
  rageActive = false;
  rageMsLeft = 0;
  rageMsTotal = 0;
  upgrades: UpgradeState = {
    damage: 10,
    critChance: 0.05,
    critMultiplier: 2,
    splitChance: 0,
    splitDiminishing: 0.6,
    splitCount: 3,
    bounceChance: 0,
    bounceDiminishing: 0.7,
    bounceMaxTargets: 1,
    attackSpeedMult: 0,
    rangeBonus: 0,
    rageChance: 0,
    rageDuration: 2.0,
  };
  skills: SkillsState = {
    moneyMultiChance: 0.1,
    moneyMultiAmount: 2,
    waveBonusMin: 10,
    waveBonusMax: 25,
  };

  // Persisted upgrade prices for DOM UI
  costs: CostsState = {
    damage: 30,
    critChance: 40,
    critMult: 40,
    splitChance: 100,
    splitDim: 50,
    splitCount: 200,
    bounceChance: 100,
    bounceDim: 110,
    bounceTargets: 200,
    attackSpeed: 100,
    range: 200,
    rageChance: 100,
    rageDuration: 100
  };

  private storageKey = 'onetd-save';

  saveToStorage() {
    try {
      const data = {
        gold: this.gold,
        // Store last completed wave so reload resumes at the same wave number
        lastCompletedWave: Math.max(0, this.wave - 1),
        upgrades: this.upgrades,
        timescale: this.timescale,
        costs: this.costs,
        // Persist vitals
        hp: this.hp,
        maxHp: this.maxHp,
        mana: this.mana,
        maxMana: this.maxMana,
      } as const;
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch {}
  }

  loadFromStorage() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const data = JSON.parse(raw) as { gold?: number; lastCompletedWave?: number; upgrades?: Partial<UpgradeState>; timescale?: number; costs?: Partial<CostsState>; hp?: number; maxHp?: number; mana?: number; maxMana?: number };
      if (typeof data.gold === 'number' && isFinite(data.gold)) this.gold = Math.max(0, Math.floor(data.gold));
      if (typeof data.lastCompletedWave === 'number' && isFinite(data.lastCompletedWave)) {
        // Resume at the saved last completed wave number (no upper cap)
        this.wave = Math.max(0, Math.floor(data.lastCompletedWave));
      }
      if (data.upgrades && typeof data.upgrades === 'object') {
        const u = { ...this.upgrades, ...data.upgrades } as UpgradeState;
        // Migrate any legacy saves that may have stored chances as 0..100 percents
        if (typeof (u as any).splitChance === 'number' && isFinite((u as any).splitChance) && (u as any).splitChance > 1) {
          (u as any).splitChance = (u as any).splitChance / 100;
        }
        u.critChance = clamp01(u.critChance);
        u.splitChance = clamp01(u.splitChance);
        u.bounceChance = clamp01(u.bounceChance);
        u.bounceMaxTargets = Math.max(1, Math.floor(u.bounceMaxTargets));
        u.damage = Math.max(1, Math.floor(u.damage));
        u.critMultiplier = Math.max(1, u.critMultiplier);
        u.splitCount = Math.max(0, Math.floor(u.splitCount));
        u.attackSpeedMult = Math.max(0, u.attackSpeedMult);
        u.rangeBonus = Math.max(0, Math.floor(u.rangeBonus));
        u.rageChance = clamp01(u.rageChance);
        u.rageDuration = Math.max(0.1, Math.min(30, u.rageDuration));
        this.upgrades = u;
      }
      if (typeof data.timescale === 'number' && isFinite(data.timescale)) {
        this.timescale = Math.max(1, Math.min(5, data.timescale));
      }
      if (data.costs && typeof data.costs === 'object') {
        const c = { ...this.costs, ...data.costs } as CostsState;
        // Ensure all numbers are positive integers where appropriate
        c.damage = Math.max(1, Math.floor(c.damage));
        c.critChance = Math.max(1, Math.floor(c.critChance));
        c.critMult = Math.max(1, Math.floor(c.critMult));
        c.splitChance = Math.max(1, Math.floor(c.splitChance));
        c.splitDim = Math.max(1, Math.floor(c.splitDim));
        c.splitCount = Math.max(1, Math.floor(c.splitCount));
        c.bounceChance = Math.max(1, Math.floor(c.bounceChance));
        c.bounceDim = Math.max(1, Math.floor(c.bounceDim));
        c.bounceTargets = Math.max(1, Math.floor(c.bounceTargets));
        c.attackSpeed = Math.max(1, Math.floor(c.attackSpeed));
        c.range = Math.max(1, Math.floor(c.range));
        c.rageChance = Math.max(1, Math.floor(c.rageChance));
        c.rageDuration = Math.max(1, Math.floor(c.rageDuration));
        this.costs = c;
      }
      // Load vitals with validation
      if (typeof data.maxHp === 'number' && isFinite(data.maxHp)) this.maxHp = Math.max(1, Math.floor(data.maxHp));
      if (typeof data.hp === 'number' && isFinite(data.hp)) this.hp = Math.max(0, Math.min(this.maxHp, Math.floor(data.hp)));
      if (typeof data.maxMana === 'number' && isFinite(data.maxMana)) this.maxMana = Math.max(1, Math.floor(data.maxMana));
      if (typeof data.mana === 'number' && isFinite(data.mana)) this.mana = Math.max(0, Math.min(this.maxMana, Math.floor(data.mana)));
    } catch {}
  }

  clearStorage() {
    try { localStorage.removeItem(this.storageKey); } catch {}
  }

  addGold(base: number) {
    // Random skill: per-kill money multiplier
    if (Math.random() < this.skills.moneyMultiChance) {
      base *= this.skills.moneyMultiAmount;
    }
    this.gold += Math.floor(base);
    this.saveToStorage();
  }

  addWaveBonus() {
    const bonus = Math.floor(
      this.skills.waveBonusMin + Math.random() * (this.skills.waveBonusMax - this.skills.waveBonusMin)
    );
    this.gold += bonus;
    this.saveToStorage();
    return bonus;
  }

  canAfford(amount: number) {
    return this.gold >= amount;
  }

  trySpend(amount: number) {
    if (this.gold >= amount) {
      this.gold -= amount;
      this.saveToStorage();
      return true;
    }
    return false;
  }
}

function clamp01(x: number) { return Math.max(0, Math.min(1, x)); }

export const gameState = new GameState();
